import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import "./Career.css";
import { MapPin, Clock, Calendar, Search, Mail, ExternalLink, Check, Menu, Upload, FileText } from "lucide-react";
import careerImg from "../../../../assets/Static/Career/career.png";
import { fetchContent } from "../../../../services/contentService";
import { COMPANY_NAME, COMPANY_EMAIL, TEXTS } from "./ConstCareer.js";
import {
  SORT_OPTIONS, LOCATION_OPTIONS, JOB_TYPE_OPTIONS,
  EXPERIENCE_OPTIONS as DEFAULT_EXPERIENCE_OPTIONS,
  AVAILABILITY_OPTIONS as DEFAULT_AVAILABILITY_OPTIONS,
  WORK_PREFERENCE_OPTIONS as DEFAULT_WORK_PREFERENCE_OPTIONS,
} from "./ConstCareer.js";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const DEFAULT_COUNTRY_CODES = [
  { code: "+1", countryName: "United States", label: "US +1" },
  { code: "+44", countryName: "United Kingdom", label: "UK +44" },
  { code: "+91", countryName: "India", label: "IN +91" },
  { code: "+971", countryName: "United Arab Emirates", label: "UAE +971" },
  { code: "+65", countryName: "Singapore", label: "SG +65" },
  { code: "+61", countryName: "Australia", label: "AU +61" },
  { code: "+49", countryName: "Germany", label: "DE +49" },
  { code: "+33", countryName: "France", label: "FR +33" },
  { code: "+81", countryName: "Japan", label: "JP +81" },
  { code: "+86", countryName: "China", label: "CN +86" },
  { code: "+7", countryName: "Russia", label: "RU +7" },
  { code: "+55", countryName: "Brazil", label: "BR +55" },
  { code: "+82", countryName: "South Korea", label: "KR +82" },
  { code: "+39", countryName: "Italy", label: "IT +39" },
  { code: "+34", countryName: "Spain", label: "ES +34" },
  { code: "+31", countryName: "Netherlands", label: "NL +31" },
  { code: "+46", countryName: "Sweden", label: "SE +46" },
  { code: "+41", countryName: "Switzerland", label: "CH +41" },
  { code: "+352", countryName: "Luxembourg", label: "LU +352" },
  { code: "+353", countryName: "Ireland", label: "IE +353" },
];

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (el) {
    const navHeight = document.querySelector(".career-nav")?.offsetHeight || 64;
    const elPosition = el.getBoundingClientRect().top + window.pageYOffset - navHeight - 20;
    window.scrollTo({ top: elPosition, behavior: "smooth" });
  }
};

const CareersNav = ({ onLogoClick }) => (
  <nav className="career-nav">
    <div className="career-nav-inner">
      <div className="career-nav-logo" onClick={onLogoClick}>
        <div className="career-nav-logo-mark">V</div>
        <span className="career-nav-brand">{COMPANY_NAME}</span>
      </div>
      <button className="career-nav-cta" onClick={() => scrollToSection("jobs")}>
        {TEXTS.OPENINGS_CTA}
      </button>
    </div>
  </nav>
);

const CareersHero = ({ onExploreClick, jobListings }) => {
  const totalJobs = jobListings.length;
  return (
    <section className="career-hero">
      <div className="career-hero-inner">
        <h1 className="career-hero-title career-fade-in career-fade-in-2"
          data-aos="fade-up" data-aos-duration="700" data-aos-delay="100"
        >
          {TEXTS.HERO_TITLE}
        </h1>
        <p className="career-hero-desc career-fade-in career-fade-in-3"
          data-aos="fade-up" data-aos-duration="700" data-aos-delay="200"
        >
          {TEXTS.HERO_DESC}
        </p>
        <div className="career-hero-actions career-fade-in career-fade-in-4"
          data-aos="fade-up" data-aos-duration="600" data-aos-delay="300"
        >
          <button className="career-btn-primary" onClick={onExploreClick}>
            {TEXTS.EXPLORE_ROLES_CTA(totalJobs)}
          </button>
          <button className="career-btn-secondary" onClick={() => scrollToSection("culture")}>
            {TEXTS.OUR_CULTURE_BTN}
          </button>
        </div>
      </div>
    </section>
  );
};

const JobCard = ({ job, onApply, index }) => (
  <div className="career-job-card"
    onClick={() => onApply(job)}
    data-aos="fade-up" data-aos-duration="500" data-aos-delay={String(Math.min(index * 80, 400))}
  >
    <div className="career-job-card-dept">{job.departmentLabel}</div>
    <div className="career-job-card-title">{job.title}</div>
    <div className="career-job-card-meta">
      <span className="career-job-card-meta-item"><MapPin size={14} className="career-job-card-meta-icon" />{job.location}</span>
      <span className="career-job-card-meta-item"><Clock size={14} className="career-job-card-meta-icon" />{job.type}</span>
      <span className="career-job-card-meta-item"><Calendar size={14} className="career-job-card-meta-icon" />{job.experience}</span>
    </div>
    <div className="career-job-card-footer">
      <span className="career-job-card-date">Posted {formatDate(job.postedDate)}</span>
      <button className="career-apply-btn" onClick={(e) => { e.stopPropagation(); onApply(job); }}>
        {TEXTS.APPLY_NOW}
      </button>
    </div>
  </div>
);

const CareersJobsSection = ({ onApply, jobListings, departments, locationOptions, jobTypeOptions, sortOptions }) => {
  const [search, setSearch] = useState("");
  const [activeDept, setActiveDept] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");
  const [locationFilter, setLocationFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [initialLimit, setInitialLimit] = useState(12);

  useEffect(() => {
    const updateLimit = () => {
      const w = window.innerWidth;
      if (w <= 640) setInitialLimit(6);
      else if (w <= 1024) setInitialLimit(9);
      else setInitialLimit(12);
    };
    updateLimit();
    window.addEventListener("resize", updateLimit);
    return () => window.removeEventListener("resize", updateLimit);
  }, []);

  const deptTabs = useMemo(() => {
    const all = [{ id: "all", label: "All Departments" }];
    if (departments?.length) {
      const rest = departments.filter(d => d.id !== "all").map(d => ({
        ...d,
        count: jobListings.filter(j => j.department === d.id).length,
      }));
      return [...all, ...rest];
    }
    return all;
  }, [departments, jobListings]);

  const filteredJobs = useMemo(() => {
    let jobs = [...jobListings];
    if (activeDept !== "all") jobs = jobs.filter((j) => j.department === activeDept);
    if (search.trim()) {
      const q = search.toLowerCase();
      jobs = jobs.filter(
        (j) => j.title.toLowerCase().includes(q) || j.departmentLabel?.toLowerCase().includes(q) || j.location?.toLowerCase().includes(q)
      );
    }
    if (locationFilter) jobs = jobs.filter((j) => j.location?.toLowerCase().includes(locationFilter.toLowerCase()));
    if (typeFilter) jobs = jobs.filter((j) => j.type === typeFilter);
    jobs.sort((a, b) => {
      const da = new Date(a.postedDate);
      const db = new Date(b.postedDate);
      return sortOrder === "newest" ? db - da : da - db;
    });
    return jobs;
  }, [search, activeDept, sortOrder, locationFilter, typeFilter, jobListings]);

  useEffect(() => { setShowAll(false); }, [search, activeDept, locationFilter, typeFilter]);

  return (
    <section className="career-search-section career-section" id="jobs">
      <div className="career-section-inner">
        <div className="career-section-header">
          <h2 className="career-section-title" data-aos="fade-up" data-aos-duration="600" data-aos-delay="80">
            {TEXTS.SECTION_TITLE_FIND_ROLE}
          </h2>
          <p className="career-section-subtitle" data-aos="fade-up" data-aos-duration="600" data-aos-delay="160">
            {TEXTS.SECTION_SUBTITLE_BROWSE}
          </p>
        </div>

        <div className="career-search-row">
          <div className="career-search-bar-wrap" data-aos="fade-up" data-aos-duration="600" data-aos-delay="200">
            <input className="career-search-input" type="text" placeholder={TEXTS.SEARCH_PLACEHOLDER}
              value={search} onChange={(e) => setSearch(e.target.value)} />
            <div className="career-search-icon-circle">
              <Search size={18} />
            </div>
          </div>
          <button className="career-hamburger-btn" onClick={() => setFilterOpen(!filterOpen)}>
            <svg width="20" height="18" viewBox="0 0 20 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="2" y1="3" x2="18" y2="3" />
              <line x1="4" y1="9" x2="16" y2="9" />
              <line x1="6" y1="15" x2="14" y2="15" />
            </svg>
          </button>
        </div>

        <div className={`career-filter-drawer-overlay ${filterOpen ? "career-filter-drawer-open" : ""}`} onClick={() => setFilterOpen(false)} />

        <div className={`career-filter-drawer ${filterOpen ? "career-filter-drawer-open" : ""}`}>
          <div className="career-filter-drawer-handle" />
          <div className="career-filter-drawer-content">
            <div className="career-filters-row" data-aos="fade-up" data-aos-duration="500" data-aos-delay="250">
              <span className="career-filter-label">{TEXTS.FILTER_LABEL}</span>
              <select className="career-filter-select" value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
                {(locationOptions || LOCATION_OPTIONS).map((o, i) => <option key={o.value || i} value={o.value || ""}>{o.label}</option>)}
              </select>
              <select className="career-filter-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                {(jobTypeOptions || JOB_TYPE_OPTIONS).map((o, i) => <option key={o.value || i} value={o.value || ""}>{o.label}</option>)}
              </select>
              {(locationFilter || typeFilter) && (
                <button className="career-filter-clear-btn" onClick={() => { setLocationFilter(""); setTypeFilter(""); }} title="Clear filters">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>

            <div className="career-dept-filter-wrap">
              <span className="career-filter-label">Department:</span>
              <select className="career-filter-select career-dept-filter-select" value={activeDept} onChange={(e) => setActiveDept(e.target.value)}>
                {deptTabs.map((dept) => (
                  <option key={dept.id} value={dept.id}>{dept.label}</option>
                ))}
              </select>
            </div>

            <div className="career-dept-tabs" data-aos="fade-up" data-aos-duration="500" data-aos-delay="300">
              {deptTabs.map((dept) => (
                <button key={dept.id}
                  className={`career-dept-tab${activeDept === dept.id ? " career-active" : ""}`}
                  onClick={() => setActiveDept(dept.id)}
                >
                  {dept.label}
                </button>
              ))}
            </div>
          </div>
          <div className="career-drawer-actions">
            <button className="career-drawer-btn career-drawer-btn-clear" onClick={() => { setActiveDept("all"); setLocationFilter(""); setTypeFilter(""); setFilterOpen(false); }}>Clear Filters</button>
            <button className="career-drawer-btn career-drawer-btn-continue" onClick={() => setFilterOpen(false)}>Continue</button>
          </div>
        </div>

        <div className="career-results-meta">
          {/* <span className="career-results-count">{TEXTS.SHOWING_RESULTS(filteredJobs.length, jobListings.length)}</span> */}
          <div className="career-sort-group">
            <span className="career-sort-label">Sort:</span>
            {(sortOptions || SORT_OPTIONS).map((o) => (
              <button key={o.value}
                className={`career-sort-btn${sortOrder === o.value ? " career-sort-btn--active" : ""}`}
                onClick={() => setSortOrder(o.value)}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {filteredJobs.length > 0 ? (
          <>
          <div className="career-jobs-grid">
{(showAll ? filteredJobs : filteredJobs.slice(0, initialLimit)).map((job, index) => (
  <JobCard key={job.id || `job-${index}`} job={job} onApply={onApply} index={index} />
))}
          </div>
          {filteredJobs.length > initialLimit && (
            <div className="career-view-toggle-wrap">
              <button className="career-view-toggle-btn" onClick={() => setShowAll(!showAll)}>
                {showAll ? "View Less" : `View More (${filteredJobs.length - initialLimit})`}
              </button>
            </div>
          )}
          </>
        ) : (
          <div style={{ textAlign: "center", padding: "56px 0", color: "var(--career-text-muted)" }} data-aos="fade-up">
            <Search size={40} style={{ marginBottom: "12px" }} />
            <div style={{ fontFamily: "var(--career-font-serif)", fontSize: "20px", color: "var(--career-navy)", marginBottom: "8px" }}>{TEXTS.NO_ROLES_TITLE}</div>
            <div style={{ fontSize: "14px" }}>{TEXTS.NO_ROLES_DESC}</div>
          </div>
        )}
      </div>
    </section>
  );
};

const CareersValuesSection = ({ culture }) => {
  const items = culture?.length ? culture : [];
  return (
    <section className="career-values-section career-section" id="culture">
      <div className="career-section-inner">
        <div className="career-section-header">
          <h2 className="career-section-title" data-aos="fade-up" data-aos-duration="600">
            {TEXTS.SECTION_TITLE_VALUES}
          </h2>
          <p className="career-section-subtitle" data-aos="fade-up" data-aos-duration="600" data-aos-delay="100">
            {TEXTS.SECTION_DESC_VALUES}
          </p>
        </div>
        <div className="career-values-grid">
          {items.map((v, i) => (
            <div className="career-value-card" key={i}
              data-aos="fade-up" data-aos-duration="600" data-aos-delay={String(i * 100)}
            >
              <div className="career-value-title">{v.title}</div>
              <p className="career-value-desc">{v.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CareersPerksSection = ({ benefits }) => {
  const items = benefits?.length ? benefits : [];
  return (
    <section className="career-perks-section career-section" id="perks">
      <div className="career-section-inner">
        <div className="career-section-header">
          <h2 className="career-section-title" data-aos="fade-up" data-aos-duration="600">
            {TEXTS.SECTION_TITLE_PERKS}
          </h2>
          <p className="career-section-subtitle" data-aos="fade-up" data-aos-duration="600" data-aos-delay="100">
            {TEXTS.SECTION_DESC_PERKS}
          </p>
        </div>
        <div className="career-perks-grid">
          {items.map((p, i) => (
            <div className="career-perk-card" key={i}
              data-aos="zoom-in" data-aos-duration="500" data-aos-delay={String(i * 70)}
            >
              <div className="career-perk-title">{p.title}</div>
              <p className="career-perk-desc">{p.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CareersHowSection = ({ howHiringWorks }) => {
  const steps = howHiringWorks?.length ? howHiringWorks : [];
  return (
    <section className="career-how-section career-section" id="how">
      <div className="career-section-inner">
        <div className="career-section-header">
          <h2 className="career-section-title" data-aos="fade-up" data-aos-duration="600">
            {TEXTS.SECTION_TITLE_HOW}
          </h2>
          <p className="career-section-subtitle" data-aos="fade-up" data-aos-duration="600" data-aos-delay="100">
            {TEXTS.SECTION_DESC_HOW}
          </p>
        </div>
        <div className="career-steps-row">
          {steps.map((s, i) => (
            <div className="career-step-card" key={s.step || i}
              data-aos="fade-up" data-aos-duration="600" data-aos-delay={String(i * 120)}
            >
              <div className="career-step-num">{s.step}</div>
              <div className="career-step-title">{s.title}</div>
              <p className="career-step-desc">{s.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CareersContactSection = () => (
  <section className="career-contact-section career-section" id="contact">
    <div className="career-section-inner">
      <div className="career-contact-card" data-aos="fade-up" data-aos-duration="700" data-aos-offset="80">
        <div>
          <div className="career-contact-illustration career-contact-illustration-mobile" data-aos="fade-up" data-aos-duration="600" data-aos-delay="100">
            <img src={careerImg} alt="Career illustration" />
          </div>
          <h2 className="career-contact-title" data-aos="fade-right" data-aos-duration="600" data-aos-delay="150">
            {TEXTS.CONTACT_TITLE}
          </h2>
          <p className="career-contact-desc" data-aos="fade-right" data-aos-duration="600" data-aos-delay="250">
            {TEXTS.CONTACT_DESC}
          </p>
          <div className="career-contact-links" data-aos="fade-right" data-aos-duration="600" data-aos-delay="350">
            <a className="career-contact-link" href={`mailto:${COMPANY_EMAIL}`}><Mail size={16} /> {COMPANY_EMAIL}</a>
            <a className="career-contact-link" href="https://linkedin.com" target="_blank" rel="noopener noreferrer"><ExternalLink size={16} /> {TEXTS.LINKEDIN_TEXT}</a>
          </div>
        </div>
        <div className="career-contact-illustration" data-aos="zoom-in" data-aos-duration="700" data-aos-delay="200">
          <img src={careerImg} alt="Career illustration" />
        </div>
      </div>
    </div>
  </section>
);

const SELECT_OPTIONS = {
  countryCode: "countryCodes",
  experience: "experienceOptions",
  availability: "availabilityOptions",
  workPreference: "workPreferenceOptions",
};

const CareersApplicationForm = ({ job, onBack, countryCodes, experienceOptions, availabilityOptions, workPreferenceOptions, formFields }) => {
  const fieldMap = useMemo(() => {
    const map = {};
    if (formFields?.length) {
      for (const f of formFields) map[f.key] = f;
    }
    return map;
  }, [formFields]);

  const fLabel = (key, fallback) => fieldMap[key]?.label || fallback;
  const fRequired = (key) => fieldMap[key]?.required === "Yes";

  const optionsFor = (key) => {
    const map = { countryCodes, experienceOptions, availabilityOptions, workPreferenceOptions };
    return map[SELECT_OPTIONS[key]] || [];
  };

  const [formData, setFormData] = useState(() => {
    const base = { resume: null, countryCode: "+91" };
    if (formFields?.length) {
      for (const f of formFields) base[f.key] = "";
    }
    return base;
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleChange = (field) => (e) => {
    let val = field === "countryCode" ? e.target.value : e.target.value;
    if (field === "phone") {
      val = val.replace(/[^0-9]/g, "");
    }
    if (field === "currentCompensation") {
      val = val.replace(/[^0-9,]/g, "");
    }
    setFormData((prev) => ({ ...prev, [field]: val }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setFormData((prev) => ({ ...prev, resume: file }));
  };

  const handleSubmit = async () => {
    const missing = (formFields || []).filter((f) => f.required === "Yes" && !formData[f.key]);
    if (missing.length > 0) {
      alert(`Please fill in the required field(s): ${missing.map((f) => f.label || f.key).join(", ")}`);
      return;
    }
    const phoneField = (formFields || []).find((f) => f.key === "phone" || f.label?.toLowerCase().includes("phone"));
    if (phoneField && formData.phone && formData.phone.replace(/\D/g, "").length !== 10) {
      alert("Please enter a valid 10-digit phone number.");
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const fd = new FormData();
      fd.append("jobId", job.id);
      fd.append("jobTitle", job.title);
      for (const key of Object.keys(formData)) {
        const val = formData[key];
        if (key === "resume") {
          if (val) fd.append("resume", val);
        } else if (key === "countryCode") {
          // combined with phone below
        } else if (key === "phone") {
          fd.append("phone", `${formData.countryCode || "+91"} ${formData.phone || ""}`);
        } else {
          fd.append(key, val || "");
        }
      }

      const res = await fetch(`${API_BASE_URL}/jobs/apply`, { method: "POST", body: fd });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
      } else {
        setSubmitError(data.message || data.error || "Submission failed. Please try again.");
      }
    } catch (err) {
      setSubmitError("Network error. Please check your connection.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="career-form-section">
        <div className="career-form-section-inner">
          <div className="career-success-card" data-aos="zoom-in" data-aos-duration="600">
            <h2 className="career-success-title">{TEXTS.SUCCESS_TITLE}</h2>
            <p className="career-success-desc">
              Thank you, <strong>{formData.fullName || formData.applicantName || "Applicant"}</strong>! We've received your application for the <strong>{job.title}</strong> role.

            </p>
            <button className="career-btn-primary" onClick={onBack}>{TEXTS.BACK_TO_ROLES}</button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="career-form-section" id="apply">
      <div className="career-form-section-inner">
        <div className="career-form-header" data-aos="fade-up" data-aos-duration="600">
          <h2 className="career-section-title">{TEXTS.FORM_TITLE}</h2>
          <p className="career-section-subtitle" style={{ margin: "0 auto" }}>{TEXTS.FORM_SUBTITLE}</p>
        </div>

        <div className="career-form-card" data-aos="fade-up" data-aos-duration="700" data-aos-delay="150">
          {(formFields || []).map((f) => {
            const opts = optionsFor(f.key);
            const isSelect = !!SELECT_OPTIONS[f.key];
            return (
              <div className="career-form-group" key={f.key} style={isSelect && opts.length <= 3 ? { display: "inline-block", width: "48%", marginRight: "4%" } : {}}>
                <label className="career-form-label">{fLabel(f.key, f.key)}{fRequired(f.key) && <span> *</span>}</label>
                {f.key === "phone" || f.label?.toLowerCase().includes("phone") ? (
                  <div className="career-phone-wrap">
                    <select className="career-country-select" value={formData.countryCode || "+91"} onChange={handleChange("countryCode")}>
                      {countryCodes.map((o, i) => <option key={o.code || o.value || i} value={o.code || o.value || ""}>{o.label}</option>)}
                    </select>
                    <input className="career-form-input career-phone-input" type="tel" inputMode="numeric" placeholder={f.placeholder || ""}
                      value={formData[f.key] || ""}
                      onChange={(e) => setFormData(prev => ({ ...prev, [f.key]: e.target.value.replace(/[^0-9]/g, "") }))} />
                  </div>
                ) : f.key === "currentCompensation" || f.label?.toLowerCase().includes("compensation") ? (
                  <div className="career-compensation-wrap">
                    <span className="career-currency-prefix">₹</span>
                    <input className="career-form-input" type="text" inputMode="decimal" placeholder={f.placeholder || ""}
                      value={formData[f.key] || ""}
                      onChange={(e) => setFormData(prev => ({ ...prev, [f.key]: e.target.value.replace(/[^0-9,]/g, "") }))} />
                  </div>
                ) : f.type === "file" || f.key === "resume" || f.label?.toLowerCase().includes("resume") || f.label?.toLowerCase().includes("cv") ? (
                  <div className="career-file-upload-wrap">
                    <input type="file" id={`field-${f.key}`} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" style={{ display: "none" }}
                      onChange={handleFileChange} />
                    <label htmlFor={`field-${f.key}`} className="career-file-upload-btn">
                      <Upload size={16} /> {f.placeholder || "Upload"}
                    </label>
                    {formData.resume && (
                      <div className="career-file-selected"><FileText size={14} /> {formData.resume.name}</div>
                    )}
                  </div>
                ) : f.type === "textarea" ? (
                  <textarea className="career-form-textarea" placeholder={f.placeholder || ""}
                    value={formData[f.key] || ""} onChange={handleChange(f.key)} rows={4} />
                ) : isSelect ? (
                  <select className="career-form-select" value={formData[f.key] || ""} onChange={handleChange(f.key)}>
                    <option value="">{f.placeholder || `Select ${f.label || f.key}`}</option>
                    {opts.map((o, i) => <option key={o.value || o.code || i} value={o.value || o.code || ""}>{o.label}</option>)}
                  </select>
                ) : (
                  <input className="career-form-input" type={f.type || "text"} placeholder={f.placeholder || ""}
                    value={formData[f.key] || ""} onChange={handleChange(f.key)} />
                )}
              </div>
            );
          })}

          {submitError && <p className="career-form-error">{submitError}</p>}

          <button className="career-form-submit" onClick={handleSubmit} disabled={submitting}>
            {submitting ? TEXTS.FORM_SUBMITTING : TEXTS.FORM_SUBMIT}
          </button>

          <p style={{ fontSize: "12px", color: "var(--career-text-muted)", textAlign: "center", marginTop: "14px", lineHeight: 1.6 }}>
            {TEXTS.FORM_PRIVACY_TEXT}
          </p>
        </div>
      </div>
    </section>
  );
};

const JobDetailPage = ({ job, onBack, hiringJourney, whyVisaCo, roleOverviewLabels, countryCodes, experienceOptions, availabilityOptions, workPreferenceOptions, formFields }) => {
  const [showShareMsg, setShowShareMsg] = useState(false);

  useEffect(() => { AOS.refresh(); }, []);

  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href).then(() => {
        setShowShareMsg(true);
        setTimeout(() => setShowShareMsg(false), 2500);
      });
    }
  };

  const overviewLabels = roleOverviewLabels?.length ? roleOverviewLabels : ["Department", "Location", "Type", "Experience", "Posted"];
  const labelToValue = {
    "Job Title": job.title,
    "Job ID": job.id,
    "Department ID": job.department,
    "Department": job.departmentLabel,
    "Department Label": job.departmentLabel,
    "Location": job.location,
    "Type": job.type,
    "Experience": job.experience,
    "Posted": formatDate(job.postedDate),
    "Posted Date": formatDate(job.postedDate),
  };
  const overviewValues = overviewLabels.map((label) => labelToValue[label] || "");
  const journeySteps = hiringJourney?.length ? hiringJourney : [];

  return (
    <div className="career-job-detail career-fade-in">
      <div className="career-detail-hero">
        <div className="career-detail-hero-inner">

          <h1 className="career-detail-title" data-aos="fade-up" data-aos-duration="600" data-aos-delay="150">
            {job.title}
          </h1>
          <div className="career-detail-meta-row" data-aos="fade-up" data-aos-duration="500" data-aos-delay="220">
            <span className="career-detail-meta-item"><MapPin size={14} /> {job.location}</span>
            <span className="career-detail-meta-item"><Clock size={14} /> {job.type}</span>
            <span className="career-detail-meta-item"><Calendar size={14} /> {job.experience}</span>
            <span className="career-detail-meta-item"><Calendar size={14} /> {TEXTS.POSTED_ON} {formatDate(job.postedDate)}</span>
          </div>
          <div className="career-detail-actions" data-aos="fade-up" data-aos-duration="500" data-aos-delay="300">
            <button className="career-btn-primary" onClick={() => scrollToSection("apply")}>
              {TEXTS.DETAIL_APPLY_BTN}
            </button>
            <button className="career-share-btn" onClick={onBack}>{TEXTS.DETAIL_ALL_ROLES_BTN}</button>
          </div>
        </div>
      </div>

      <div className="career-detail-content">
        <div className="career-detail-main">
          {[
            { title: TEXTS.SECTION_JOB_DESCRIPTION, content: <p className="career-detail-text">{job.description}</p> },
            { title: TEXTS.SECTION_THE_ROLE, content: <p className="career-detail-text">{job.role}</p> },
            {
              title: TEXTS.SECTION_REQUIREMENTS,
              content: (
                <ul className="career-detail-list">
                  {(job.requirements || "").split("\n").filter(Boolean).map((req, i) => (
                    <li key={i} data-aos="fade-left" data-aos-duration="400" data-aos-delay={String(i * 60)} data-aos-offset="20">
                      {req.trim()}
                    </li>
                  ))}
                </ul>
              ),
            },
            { title: TEXTS.SECTION_IDEAL_CANDIDATE, content: <p className="career-detail-text">{job.idealCandidate}</p> },
          ].map(({ title, content }, i) => (
            <div className="career-detail-section" key={title}
              data-aos="fade-up" data-aos-duration="600" data-aos-offset="60" data-aos-delay={String(i * 50)}
            >
              <h3 className="career-detail-section-title">{title}</h3>
              {content}
            </div>
          ))}
        </div>

        <div className="career-detail-sidebar">
          <div className="career-sidebar-card" data-aos="fade-left" data-aos-duration="600" data-aos-delay="200">
            <div className="career-sidebar-card-title">{TEXTS.SIDEBAR_ROLE_OVERVIEW}</div>
            {overviewLabels.map((label, idx) => (
              <div className="career-sidebar-info-row" key={label}>
                <span className="career-sidebar-label">{label}</span>
                <span className="career-sidebar-value">{overviewValues[idx] || ""}</span>
              </div>
            ))}
          </div>

          <div className="career-sidebar-card" data-aos="fade-left" data-aos-duration="600" data-aos-delay="300">
            <div className="career-sidebar-card-title">{TEXTS.SIDEBAR_WHY_COMPANY(COMPANY_NAME)}</div>
            {(whyVisaCo || []).map((item, i) => (
              <div className="career-sidebar-info-row" key={i}>
                <Check size={14} style={{ color: "var(--career-accent)", flexShrink: 0 }} />
                <span className="career-sidebar-value" style={{ textAlign: "left" }}>{item}</span>
              </div>
            ))}
          </div>

          <div style={{ padding: "0 4px" }} data-aos="fade-up" data-aos-duration="500" data-aos-delay="400">
            <button className="career-btn-primary" style={{ width: "100%", textAlign: "center" }}
              onClick={() => scrollToSection("apply")}
            >
              {TEXTS.DETAIL_APPLY_BTN}
            </button>
          </div>
        </div>
      </div>

      <CareersApplicationForm job={job} onBack={onBack} countryCodes={countryCodes}
        experienceOptions={experienceOptions} availabilityOptions={availabilityOptions}
        workPreferenceOptions={workPreferenceOptions} formFields={formFields}
      />
    </div>
  );
};

export default function CareersPage() {
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobListings, setJobListings] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [culture, setCulture] = useState([]);
  const [howHiringWorks, setHowHiringWorks] = useState([]);
  const [hiringJourney, setHiringJourney] = useState([]);
  const [whyVisaCo, setWhyVisaCo] = useState([]);
  const [roleOverviewLabels, setRoleOverviewLabels] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [countryCodes, setCountryCodes] = useState(DEFAULT_COUNTRY_CODES);
  const [experienceOptions, setExperienceOptions] = useState(DEFAULT_EXPERIENCE_OPTIONS);
  const [availabilityOptions, setAvailabilityOptions] = useState(DEFAULT_AVAILABILITY_OPTIONS);
  const [workPreferenceOptions, setWorkPreferenceOptions] = useState(DEFAULT_WORK_PREFERENCE_OPTIONS);
  const [formFields, setFormFields] = useState([]);
  const [locationOptions, setLocationOptions] = useState(LOCATION_OPTIONS);
  const [jobTypeOptions, setJobTypeOptions] = useState(JOB_TYPE_OPTIONS);
  const [sortOptions, setSortOptions] = useState(SORT_OPTIONS);
  const [loaded, setLoaded] = useState(false);
  const versionRef = useRef(null);

  const applyContent = useCallback((section) => {
    if (!section?.content) return;
    const c = section.content;
    if (c.jobListings?.length) setJobListings(c.jobListings);
    if (c.departments?.length) setDepartments(c.departments);
    if (c.culture?.length) setCulture(c.culture);
    if (c.howHiringWorks?.length) setHowHiringWorks(c.howHiringWorks);
    if (c.hiringJourney?.length) setHiringJourney(c.hiringJourney);
    if (c.whyVisaCo?.length) setWhyVisaCo(c.whyVisaCo);
    if (c.roleOverviewLabels?.length) setRoleOverviewLabels(c.roleOverviewLabels);
    if (c.benefits?.length) setBenefits(c.benefits);
    if (c.countryCodes?.length) setCountryCodes(c.countryCodes);
    if (c.experienceOptions?.length) setExperienceOptions(c.experienceOptions);
    if (c.availabilityOptions?.length) setAvailabilityOptions(c.availabilityOptions);
    if (c.workPreferenceOptions?.length) setWorkPreferenceOptions(c.workPreferenceOptions);
    if (c.formFields?.length) setFormFields(c.formFields.map(f => ({
      ...f,
      key: (f.key || "").trim(),
      label: (f.label || "").trim(),
      placeholder: (f.placeholder || "").trim(),
    })));
    if (c.locationOptions?.length) setLocationOptions(c.locationOptions);
    if (c.jobTypeOptions?.length) setJobTypeOptions(c.jobTypeOptions);
    if (c.sortOptions?.length) setSortOptions(c.sortOptions);
    if (section.version != null) versionRef.current = section.version;
  }, []);

  useEffect(() => {
    let active = true;
    const fetchAndApply = () => {
      fetchContent("visaCareer").then((section) => {
        if (!active) return;
        if (section?.content) {
          if (section.version != null && versionRef.current != null && section.version === versionRef.current) {
            if (!loaded) setLoaded(true);
            return;
          }
          applyContent(section);
        }
        if (!loaded) setLoaded(true);
      });
    };
    fetchAndApply();
    const interval = setInterval(fetchAndApply, 30000);
    const onFocus = () => fetchAndApply();
    window.addEventListener("focus", onFocus);
    return () => {
      active = false;
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [applyContent, loaded]);

  useEffect(() => {
    AOS.init({ duration: 600, easing: "ease-out-cubic", once: true, offset: 80, delay: 0 });
  }, []);

  useEffect(() => { AOS.refresh(); }, [selectedJob]);

  const handleApply = (job) => {
    setSelectedJob(job);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    setSelectedJob(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!loaded) {
    return <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", color: "var(--career-text-muted)" }}>Loading...</div>;
  }

  return (
    <div className="career-page-root">
      {selectedJob ? (
        <JobDetailPage job={selectedJob} onBack={handleBack}
          hiringJourney={hiringJourney} whyVisaCo={whyVisaCo} roleOverviewLabels={roleOverviewLabels}
          countryCodes={countryCodes} experienceOptions={experienceOptions}
          availabilityOptions={availabilityOptions} workPreferenceOptions={workPreferenceOptions}
          formFields={formFields}
        />
      ) : (
        <>
          <CareersHero onExploreClick={() => scrollToSection("jobs")} jobListings={jobListings} />
          <CareersJobsSection onApply={handleApply} jobListings={jobListings} departments={departments}
            locationOptions={locationOptions} jobTypeOptions={jobTypeOptions} sortOptions={sortOptions} />
          <CareersPerksSection benefits={benefits} />
          <CareersValuesSection culture={culture} />
          <CareersHowSection howHiringWorks={howHiringWorks} />
          <CareersContactSection />
        </>
      )}
    </div>
  );
}
